import {
  useAccountingGetCorrectiveTaxDocumentItemsQuery,
  useGetListCorrectiveTaxDocumentSettingQuery,
} from '@dms/api/finance';

import {useQueryState} from 'shared/web';

import {useGetCurrentBranch} from './useGetCurrentBranch';

export const useGetBalanceInvoiceCorrectiveItems = (
  isBalanceInvoiceFromCheckout: boolean,
  id: string | undefined
) => {
  const [type] = useQueryState('type');
  const {activeBranchId} = useGetCurrentBranch();
  const {
    data: correctiveTaxDocumentSetting,
    isLoading: isLoadingCorrectiveTaxDocumentSetting,
    isError: isCorrectiveTaxDocumentSettingError,
    error: correctiveTaxDocumentSettingError,
  } = useGetListCorrectiveTaxDocumentSettingQuery({}, {skip: !isBalanceInvoiceFromCheckout});

  const {
    data: balanceInvoiceItemsData,
    isLoading: isLoadingBalanceInvoiceItems,
    isError: isBalanceInvoiceItemsError,
    error: balanceInvoiceItemsError,
  } = useAccountingGetCorrectiveTaxDocumentItemsQuery(
    {balanceInvoiceId: id!},
    {skip: !isBalanceInvoiceFromCheckout}
  );

  const isLoadingBalanceItems =
    isLoadingBalanceInvoiceItems || isLoadingCorrectiveTaxDocumentSetting;
  const isBalanceItemsError = isBalanceInvoiceItemsError || isCorrectiveTaxDocumentSettingError;
  const balanceItemsError = correctiveTaxDocumentSettingError ?? balanceInvoiceItemsError ?? null;

  const activeSetting = correctiveTaxDocumentSetting?.find(
    (setting) => setting.branchId === activeBranchId
  );

  const systemSetting = correctiveTaxDocumentSetting?.find((setting) => setting.isSystem);

  // Setting-driven decision only - purely a function of `activeSetting`/`systemSetting`, with no
  // `type` involved. Exposed separately from the final filtered list below because the
  // `type === 'partial'` half of the decision isn't always known at this hook's render time (the
  // direct-checkout flow only learns the user's chosen type at click time, inside its own
  // submit handler) - such a caller applies this setting-driven flag together with its own
  // click-time type instead of relying on the filtered list below.
  const shouldExcludeProformaDeductionBySetting =
    (activeSetting ?? systemSetting)?.createCorrectiveTaxDocumentType === 'without_proforma';

  const rawBalanceInvoiceItems = balanceInvoiceItemsData?.items;

  // Default, render-time-only filtered list - used as-is by callers that know `type` at render
  // time (the manual form route, which reads it from the URL via `useQueryState` above).
  const balanceInvoiceItems = rawBalanceInvoiceItems?.filter((item) =>
    shouldExcludeProformaDeductionBySetting || type === 'partial'
      ? item.generatedItemType !== 'proforma_deduction'
      : true
  );

  return {
    balanceInvoiceItems,
    rawBalanceInvoiceItems,
    isLoadingBalanceItems,
    isBalanceItemsError,
    balanceItemsError,
    shouldExcludeProformaDeductionBySetting,
  };
};
